import { API_CONFIG, API_ENDPOINTS } from '../config/api';
import { logger } from '../utils/logger';
import {
  getAccessToken,
  getRefreshToken,
  updateTokens,
  clearTokens as clearAuthTokens,
} from '../utils/authStorage';

interface LoginResponse {
  access: string;
  refresh: string;
}

export interface RegisterData {
  email: string;
  password: string;
  passwordConfirm: string;
  firstName: string;
  lastName: string;
  termsAccepted: boolean;
}

/** Payload enviado ao backend no registro (snake_case). */
export interface RegisterBackendPayload {
  email: string;
  password: string;
  password_confirm: string;
  first_name: string;
  last_name: string;
  terms_accepted: boolean;
  privacy_policy_accepted: boolean;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  createdAt: string;
  isSuperuser: boolean;
  isStaff: boolean;
}

class AuthService {
  private isRefreshing = false;
  private refreshPromise: Promise<LoginResponse> | null = null;

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retry = true
  ): Promise<T> {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;
    
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add authorization header if token exists
    const token = getAccessToken();
    if (token) {
      defaultHeaders.Authorization = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      logger.log('[authService] Fazendo requisição:', { url, method: config.method || 'GET' });
      
      // Garantir que não há signal no config (pode causar problemas com hot reload)
      const cleanConfig: RequestInit & { signal?: AbortSignal } = { ...config };
      if ('signal' in cleanConfig) {
        delete cleanConfig.signal;
        logger.log('[authService] Removido signal do config');
      }
      
      let response: Response;
      try {
        logger.log('[authService] Aguardando resposta do fetch...');
        logger.log('[authService] Config completo:', {
          url,
          method: cleanConfig.method,
          headers: cleanConfig.headers,
          body: cleanConfig.body ? (typeof cleanConfig.body === 'string' ? cleanConfig.body.substring(0, 100) + '...' : 'Object') : undefined,
          hasSignal: 'signal' in cleanConfig
        });
        
        // Fazer fetch com timeout
        const fetchPromise = fetch(url, cleanConfig);
        logger.log('[authService] Promise do fetch criada, aguardando...');
        
        // Adicionar timeout de 10 segundos
        const timeoutPromise = new Promise<Response>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Timeout: A requisição demorou mais de 10 segundos'));
          }, 10000);
        });
        
        response = await Promise.race([fetchPromise, timeoutPromise]) as Response;
        logger.log('[authService] Fetch completado, resposta recebida! Status:', response.status);
      } catch (fetchError: unknown) {
        const err = fetchError instanceof Error ? fetchError : new Error(String(fetchError));
        logger.error('[authService] Erro capturado no fetch:', fetchError);
        logger.error('[authService] Tipo do erro:', err.constructor.name);
        logger.error('[authService] Detalhes do erro:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });
        
        // Verificar se é erro de CORS
        if (err.message && err.message.includes('CORS')) {
          throw new Error('Erro de CORS. Verifique as configurações do backend.');
        }
        
        throw new Error('Erro de rede ao fazer requisição: ' + (err.message || 'Erro desconhecido'));
      }
      
      if (!response) {
        logger.error('[authService] Resposta é null ou undefined!');
        throw new Error('Resposta vazia do servidor');
      }
      
      logger.log('[authService] Resposta recebida:', { 
        status: response.status, 
        statusText: response.statusText,
        ok: response.ok,
        type: response.type,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      // Se receber 401 (Unauthorized), tentar renovar o token automaticamente
      if (response.status === 401 && retry && endpoint !== API_ENDPOINTS.REFRESH_TOKEN) {
        try {
          await this.refreshAccessToken();
          const newToken = getAccessToken();
          if (newToken) {
            config.headers = {
              ...config.headers,
              Authorization: `Bearer ${newToken}`,
            };
            const retryResponse = await fetch(url, config);
            if (!retryResponse.ok) {
              const errorData = await retryResponse.json().catch(() => ({}));
              throw new Error(errorData.detail || errorData.message || `HTTP ${retryResponse.status}`);
            }
            return await retryResponse.json();
          }
        } catch (refreshError) {
          clearAuthTokens();
          throw new Error('Sessão expirada. Por favor, faça login novamente.');
        }
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        logger.log('[authService] Erro na resposta:', {
          status: response.status,
          statusText: response.statusText,
          errorData,
          errorDataString: JSON.stringify(errorData, null, 2)
        });
        
        // Mapeamento de mensagens de erro do Django para português
        const translateError = (msg: unknown): string => {
          // Converter para string se não for string
          const msgStr = typeof msg === 'string' ? msg : String(msg ?? 'Erro desconhecido');
          
          // Remover prefixos numéricos como "1. " ou "2. " do início da mensagem
          const cleanMsg = msgStr.replace(/^\d+\.\s*/, '').trim();
          
          const translations: Record<string, string> = {
            'This password is too short. It must contain at least 8 characters.': 
              'Senha muito curta. Deve conter pelo menos 8 caracteres.',
            'This password is too common.': 
              'Senha muito comum. Use uma combinação mais única.',
            'This password is entirely numeric.': 
              'Senha não pode conter apenas números. Adicione letras e símbolos.',
            'This field is required.': 
              'Este campo é obrigatório.',
            'Enter a valid email address.': 
              'Digite um endereço de email válido.',
            'A user with that email already exists.': 
              'Usuário com este email já existe.',
            'Usuários with this email already exists.': 
              'Usuário com este email já existe.',
            'user with this email already exists.': 
              'Usuário com este email já existe.',
            'Usuários com este email já existe.': 
              'Usuário com este email já existe.',
            'Usuários com este email já existe': 
              'Usuário com este email já existe.',
            'Email não encontrado.': 
              'Email não encontrado. Verifique se o email está correto.',
            'Email not found.': 
              'Email não encontrado. Verifique se o email está correto.',
            'No active account found with the given credentials': 
              'Email ou senha incorretos.',
            'No active account found with the given credentials.': 
              'Email ou senha incorretos.',
          };
          
          // Tentar traduzir a mensagem limpa
          return translations[cleanMsg] || translations[msgStr] || msgStr;
        };
        
        // Se houver erros de validação detalhados
        if (errorData.password || errorData.email || errorData.non_field_errors || errorData.password_confirm || 
            errorData.terms_accepted || errorData.privacy_policy_accepted || errorData.first_name || errorData.last_name) {
          const errors = [];
          
          if (errorData.password) {
            const passwordErrors = Array.isArray(errorData.password) ? errorData.password : [errorData.password];
            errors.push(...passwordErrors.map((err: unknown) => translateError(err)));
          }
          if (errorData.password_confirm) {
            const confirmErrors = Array.isArray(errorData.password_confirm) ? errorData.password_confirm : [errorData.password_confirm];
            errors.push(...confirmErrors.map((err: unknown) => translateError(err)));
          }
          if (errorData.email) {
            const emailErrors = Array.isArray(errorData.email) ? errorData.email : [errorData.email];
            errors.push(...emailErrors.map((err: unknown) => translateError(err)));
          }
          if (errorData.first_name) {
            const firstNameErrors = Array.isArray(errorData.first_name) ? errorData.first_name : [errorData.first_name];
            errors.push(...firstNameErrors.map((err: unknown) => translateError(err)));
          }
          if (errorData.last_name) {
            const lastNameErrors = Array.isArray(errorData.last_name) ? errorData.last_name : [errorData.last_name];
            errors.push(...lastNameErrors.map((err: unknown) => translateError(err)));
          }
          if (errorData.terms_accepted) {
            const termsErrors = Array.isArray(errorData.terms_accepted) ? errorData.terms_accepted : [errorData.terms_accepted];
            errors.push(...termsErrors.map((err: unknown) => translateError(err)));
          }
          if (errorData.privacy_policy_accepted) {
            const privacyErrors = Array.isArray(errorData.privacy_policy_accepted) ? errorData.privacy_policy_accepted : [errorData.privacy_policy_accepted];
            errors.push(...privacyErrors.map((err: unknown) => translateError(err)));
          }
          if (errorData.non_field_errors) {
            const fieldErrors = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors : [errorData.non_field_errors];
            errors.push(...fieldErrors.map((err: unknown) => translateError(err)));
          }
          
          throw new Error(errors.join('\n'));
        }
        
        // Função auxiliar para extrair mensagens de erro de objetos aninhados
        const extractErrorMessage = (obj: unknown): string | null => {
          if (!obj) return null;
          
          // Se for string, retornar diretamente
          if (typeof obj === 'string') {
            return obj;
          }
          
          // Se for array, extrair mensagens de cada item
          if (Array.isArray(obj)) {
            const messages = obj
              .map(item => extractErrorMessage(item))
              .filter((msg): msg is string => msg !== null);
            return messages.length > 0 ? messages.join('\n') : null;
          }
          
          // Se for objeto, tentar extrair de campos comuns
          if (typeof obj === 'object') {
            const o = obj as Record<string, unknown>;
            // Se o objeto for serializer.errors do Django (objeto com campos de validação)
            // Extrair mensagens de cada campo
            const fieldMessages: string[] = [];
            for (const [key, value] of Object.entries(o)) {
              const msg = extractErrorMessage(value);
              if (msg) {
                // Adicionar nome do campo se for um erro de campo específico
                if (key !== 'detail' && key !== 'message' && key !== 'error' && key !== 'non_field_errors') {
                  fieldMessages.push(`${key}: ${msg}`);
                } else {
                  fieldMessages.push(msg);
                }
              }
            }
            if (fieldMessages.length > 0) {
              return fieldMessages.join('\n');
            }
            
            // Tentar campos comuns como fallback
            if (o.detail) {
              const detailMsg = extractErrorMessage(o.detail);
              if (detailMsg) return detailMsg;
            }
            if (o.message) {
              const messageMsg = extractErrorMessage(o.message);
              if (messageMsg) return messageMsg;
            }
            if (o.error) {
              const errorMsg = extractErrorMessage(o.error);
              if (errorMsg) return errorMsg;
            }
          }
          
          return null;
        };
        
        // Traduzir mensagens de erro gerais (detail, message, error)
        // Verificar se há mensagem de erro no formato do Django REST Framework
        // Caso especial: se errorData.detail é um objeto (serializer.errors), extrair dele
        let errorMsg: string | null = null;
        
        if (errorData.detail && typeof errorData.detail === 'object' && !Array.isArray(errorData.detail)) {
          // errorData.detail é um objeto com erros de validação (ex: {email: [...], password: [...]})
          errorMsg = extractErrorMessage(errorData.detail);
        } else {
          // Tentar extrair de errorData diretamente
          errorMsg = extractErrorMessage(errorData);
        }
        
        // Se não houver mensagem específica, verificar se há erros de campo diretamente em errorData
        if (!errorMsg && typeof errorData === 'object') {
          // Tentar extrair mensagens de campos específicos
          const fieldMessages: string[] = [];
          for (const [key, value] of Object.entries(errorData)) {
            // Ignorar campos que já foram verificados
            if (['detail', 'message', 'error'].includes(key)) continue;
            
            const msg = extractErrorMessage(value);
            if (msg) fieldMessages.push(msg);
          }
          if (fieldMessages.length > 0) {
            errorMsg = fieldMessages.join('\n');
          }
        }
        
        if (!errorMsg) {
          errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        }
        
        throw new Error(translateError(errorMsg));
      }

      // Tentar fazer parse do JSON, mas se não houver conteúdo, retornar void
      const contentType = response.headers.get('content-type');
      logger.log('[authService] Processando resposta bem-sucedida. Status:', response.status, response.statusText);
      logger.log('[authService] Content-Type:', contentType);
      
      // Para status 201 (Created) ou 200 (OK), sempre tentar parsear JSON
      if (response.status === 201 || response.status === 200) {
        try {
          // Verificar se há conteúdo para ler
          const text = await response.text();
          // Log apenas tamanho e tipo, não o conteúdo completo (segurança)
          logger.log('[authService] Resposta recebida:', { 
            size: text.length, 
            hasContent: !!text.trim(),
            contentType: 'application/json'
          });
          
          if (text && text.trim()) {
            try {
              const data = JSON.parse(text);
              // Log apenas estrutura, não dados sensíveis
              logger.log('[authService] Resposta JSON parseada com sucesso. Campos:', Object.keys(data));
              return data;
            } catch (parseError) {
              logger.error('[authService] Erro ao fazer parse do JSON:', parseError);
              // Se não conseguir fazer parse, retornar um objeto padrão
              return { message: 'Registro realizado com sucesso', user_id: '' } as T;
            }
          } else {
            logger.log('[authService] Resposta vazia, retornando objeto padrão');
            return { message: 'Registro realizado com sucesso', user_id: '' } as T;
          }
        } catch (e) {
          logger.error('[authService] Erro ao processar resposta:', e);
          // Se não conseguir fazer parse, retornar um objeto padrão
          return { message: 'Registro realizado com sucesso', user_id: '' } as T;
        }
      }
      
      // Para outros status codes, verificar Content-Type
      if (contentType && contentType.includes('application/json')) {
        try {
          const data = await response.json();
          // Log apenas estrutura, não dados sensíveis
          logger.log('[authService] Resposta JSON parseada com sucesso. Campos:', Object.keys(data));
          return data;
        } catch (e) {
          logger.error('[authService] Erro ao fazer parse do JSON:', e);
          return undefined as T;
        }
      }
      
      // Se não houver conteúdo JSON, retornar void
      logger.log('[authService] Resposta sem conteúdo JSON');
      return undefined as T;
    } catch (error) {
      logger.error('[authService] Erro capturado no request:', error);
      if (error instanceof Error) {
        logger.error('[authService] Erro é uma instância de Error:', error.message, error.stack);
        throw error;
      }
      logger.error('[authService] Erro desconhecido:', error);
      throw new Error('Erro de rede');
    }
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    return this.request<LoginResponse>(API_ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(data: RegisterData): Promise<{ user_id: string; message: string }> {
    // Mapear os campos para o formato esperado pelo backend
    const backendData: RegisterBackendPayload = {
      email: data.email,
      password: data.password,
      password_confirm: data.passwordConfirm,
      first_name: data.firstName,
      last_name: data.lastName,
      terms_accepted: data.termsAccepted,
      privacy_policy_accepted: data.termsAccepted, // Mesmo valor, aceita ambos
    };
    
    logger.log('[authService] Dados sendo enviados para registro:', { ...backendData, password: '***', password_confirm: '***' });
    
    const response = await this.request<{ user_id: string; message: string }>(API_ENDPOINTS.REGISTER, {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
    
    logger.log('[authService] Resposta do registro:', response);
    
    return response;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.request<any>(API_ENDPOINTS.USER_PROFILE);
    // Map snake_case from backend to camelCase for frontend
    return {
      id: response.id,
      email: response.email,
      firstName: response.first_name || '',
      lastName: response.last_name || '',
      emailVerified: response.email_verified || false,
      createdAt: response.created_at,
      isSuperuser: response.is_superuser || false,
      isStaff: response.is_staff || false
    };
  }

  async refreshToken(refresh: string): Promise<LoginResponse> {
    return this.request<LoginResponse>(API_ENDPOINTS.REFRESH_TOKEN, {
      method: 'POST',
      body: JSON.stringify({ refresh }),
    }, false); // Não tentar refresh recursivo
  }

  private async refreshAccessToken(): Promise<void> {
    if (this.isRefreshing && this.refreshPromise) {
      await this.refreshPromise;
      return;
    }

    this.isRefreshing = true;
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      this.isRefreshing = false;
      throw new Error('No refresh token available');
    }

    this.refreshPromise = this.refreshToken(refreshToken);

    try {
      const response = await this.refreshPromise;
      updateTokens(response.access, response.refresh);
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Tenta renovar o access token usando o refresh token.
   * Para uso por outros serviços (ex.: geminiProxyService) quando recebem 401.
   * Em caso de falha, limpa os tokens e lança erro amigável.
   */
  async tryRefreshToken(): Promise<void> {
    try {
      await this.refreshAccessToken();
    } catch {
      clearAuthTokens();
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
  }

  async verifyEmail(email: string, code: string): Promise<void> {
    return this.request<void>(API_ENDPOINTS.VERIFY_EMAIL, {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  }

  async resendVerificationEmail(email: string): Promise<{ message: string }> {
    logger.log('[authService] Reenviando código de verificação para:', email);
    return this.request<{ message: string }>(API_ENDPOINTS.RESEND_VERIFICATION_EMAIL, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async requestPasswordReset(email: string): Promise<void> {
    return this.request<void>(API_ENDPOINTS.PASSWORD_RESET, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(email: string, code: string, newPassword: string, newPasswordConfirm: string): Promise<void> {
    return this.request<void>(API_ENDPOINTS.PASSWORD_RESET_CONFIRM, {
      method: 'POST',
      body: JSON.stringify({ 
        email,
        code,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm
      }),
    });
  }

  async changePassword(oldPassword: string, newPassword: string, newPasswordConfirm: string): Promise<void> {
    return this.request<void>(API_ENDPOINTS.CHANGE_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ 
        old_password: oldPassword, 
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm
      }),
    });
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    return this.request<User>(API_ENDPOINTS.USER_PROFILE, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  isAuthenticated(): boolean {
    return !!getAccessToken();
  }

  getToken(): string | null {
    return getAccessToken();
  }
}

export const authService = new AuthService();
